"use client";

import { useRouter, useSearchParams } from "next/navigation";
import { ActionButton } from "@stone-payments/jade";
import { type Category } from "@/actions/schemas/categories";
import { useFilters } from "@/contexts/filters-provider";
import { useEffect } from "react";
import { storeAnalitica } from "@/app/(store)/_utils/events";
import { buildQueryString } from "@/utils/url";

export type CategoriesSelectorProps = {
  categories: Category[];
  selected?: string;
};

export const CategoriesSelector = ({
  categories,
  selected,
}: CategoriesSelectorProps) => {
  const router = useRouter();
  const searchParams = useSearchParams();

  const {
    setIsFiltering,
    categoryId: current,
    setCategoryId: setCurrent,
    setSearchTerm,
  } = useFilters();

  const handleSelect = async (categoryId: string) => {
    storeAnalitica.events.action.categoryClicked(categoryId === "");

    setIsFiltering(true);
    setSearchTerm("");
    setCurrent(categoryId);

    const { queryString } = buildQueryString({
      ...Object.fromEntries(searchParams),
      busca: null,
      categoria: categoryId || null,
    });

    router.push(queryString);
  };

  useEffect(() => {
    setCurrent(selected || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  if (categories.length === 0) {
    return null;
  }

  return (
    <section
      data-testid="section-categories"
      className="-mr-5 flex min-w-0 gap-2 overflow-x-auto pr-2"
    >
      <div className="w-max">
        <ActionButton
          active={!current}
          onClick={() => handleSelect("")}
          data-testid="category-all"
        >
          Todos
        </ActionButton>
      </div>

      {categories?.map((category) => {
        const { id, name } = category!;

        return (
          <div key={id} className="w-max shrink-0 grow-0">
            <ActionButton
              active={id === current}
              onClick={() => handleSelect(id)}
            >
              {name}
            </ActionButton>
          </div>
        );
      })}
    </section>
  );
};
